<template>
  <!-- Settings gear + display settings panel at the top of the sidebar (App.vue, which also
       holds its styles: .ff-gear, .ff-menu, ...). The settings belong to the user
       (helpers/uiPrefs.js); the panel also carries the account and Log Out. The user's name
       sits before the gear and opens the panel too. -->
  <div class="ff-user-wrap" ref="root">
    <span class="ff-nav-user" @click="open = !open">{{ user }}</span>
    <button class="ff-gear" :class="{ 'is-open': open }" :title="$t('common.menu.title')" @click="open = !open">
      <svg viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    </button>
    <div v-if="open" class="ff-menu">
      <div class="ff-menu-account">
        <span class="ff-avatar">{{ String(user || '?').charAt(0).toUpperCase() }}</span>
        <span class="ff-user-name">{{ user }}</span>
        <a class="ff-btn" @click="$emit('logout')">Log Out</a>
      </div>
      <div class="ff-menu-sec">
        <div class="ff-menu-label">{{ $t('common.menu.language') }}</div>
        <div class="ff-seg">
          <button
            v-for="l in LANGUAGES"
            :key="l.key"
            :class="{ 'is-on': ui.lang === l.key }"
            @click="set('lang', l.key)"
          >{{ l.label }}</button>
        </div>
      </div>
      <div class="ff-menu-sec">
        <div class="ff-menu-label">{{ $t('common.menu.nav') }}</div>
        <div class="ff-seg ff-menu-nav">
          <button
            v-for="n in NAV_PLACES"
            :key="n.key"
            :class="{ 'is-on': ui.nav === n.key }"
            @click="set('nav', n.key)"
          >{{ $t('common.menu.' + n.i18n) }}</button>
        </div>
      </div>
      <div class="ff-menu-sec">
        <div class="ff-menu-label">{{ $t('common.menu.orderHist') }}</div>
        <div class="ff-seg">
          <button
            v-for="v in ORDER_HIST_VARIANTS.filter(v => !v.hidden)"
            :key="v.key"
            :class="{ 'is-on': ui.orderHist === v.key }"
            :title="$t('common.menu.' + v.i18n + 'Title')"
            @click="set('orderHist', v.key)"
          >{{ $t('common.menu.' + v.i18n) }}</button>
        </div>
      </div>
      <div class="ff-menu-sec">
        <div class="ff-menu-label">{{ $t('common.menu.textSize') }}</div>
        <div class="ff-seg">
          <button
            v-for="f in FONT_SIZES"
            :key="f.key"
            :class="{ 'is-on': ui.font === f.key }"
            :title="$t('common.menu.' + f.i18n)"
            :style="{ fontSize: (12 * f.scale) + 'px' }"
            @click="set('font', f.key)"
          >A</button>
        </div>
      </div>
      <div class="ff-menu-sec">
        <div class="ff-menu-label">{{ $t('common.menu.colorTheme') }}</div>
        <div class="ff-swatches">
          <button
            v-for="t in THEMES"
            :key="t.key"
            class="ff-swatch"
            :class="{ 'is-on': ui.theme === t.key }"
            @click="set('theme', t.key)"
          >
            <span class="ff-swatch-dot" :style="{ background: t.swatch }"></span>{{ t.label }}
          </button>
          <label class="ff-swatch ff-swatch-custom" :class="{ 'is-on': ui.theme === 'custom' }" :title="$t('common.menu.customTitle')">
            <span class="ff-swatch-dot ff-swatch-picker" :style="{ background: ui.custom }">
              <input type="color" :value="ui.custom" @input="setCustom($event.target.value)" @click="set('theme', 'custom')">
            </span>{{ $t('common.menu.custom') }}
          </label>
        </div>
      </div>
      <!-- Style: page, panel, text and outline colours. Off is the standard look; on, pick a
           preset or change any of the four colours, which makes the style the user's own -->
      <div class="ff-menu-sec ff-menu-style">
        <div class="ff-menu-head">
          <div class="ff-menu-label">{{ $t('common.menu.style') }}</div>
          <div class="ff-seg ff-seg-sm">
            <button :class="{ 'is-on': !ui.styleOn }" @click="set('styleOn', false)">{{ $t('common.menu.styleOff') }}</button>
            <button :class="{ 'is-on': ui.styleOn }" @click="set('styleOn', true)">{{ $t('common.menu.styleOnLabel') }}</button>
          </div>
        </div>
        <template v-if="ui.styleOn">
          <div v-for="row in styleRows" :key="row.level" class="ff-style-row">
            <span class="ff-style-level">{{ $t('common.menu.styleLevel_' + row.level) }}</span>
            <button
              v-for="s in row.presets"
              :key="s.key"
              class="ff-style-tile"
              :class="{ 'is-on': ui.style === s.key }"
              :style="{ background: s.paper }"
              :title="presetName(s)"
              @click="set('style', s.key)"
            >
              <span class="ff-style-panel" :style="{ background: s.surface, borderColor: s.line, color: s.ink }">Aa</span>
            </button>
          </div>
          <div class="ff-style-current">{{ ui.style === 'custom' ? $t('common.menu.styleCustom') : presetName(currentPreset) }}</div>
          <div class="ff-style-edit">
            <label v-for="k in STYLE_EDITABLE" :key="k" class="ff-style-color">
              <span class="ff-swatch-dot ff-swatch-picker ff-swatch-bg" :style="{ background: tokens[k] }">
                <input type="color" :value="tokens[k]" @input="editStyle(k, $event.target.value)">
              </span>
              <span>{{ $t('common.menu.styleColor_' + k) }}</span>
              <small v-if="checks[k]" class="ff-ratio" :class="{ 'is-low': checks[k].low }" :title="checks[k].low ? $t('common.menu.styleLow') : ''">{{ checks[k].text }}</small>
            </label>
          </div>
          <div class="ff-menu-hint">{{ $t('common.menu.styleHint') }}</div>
        </template>
      </div>
      <div class="ff-menu-note">
        {{ $t('common.menu.note') }}
      </div>
    </div>
  </div>
</template>

<script>
import { THEMES, FONT_SIZES, ORDER_HIST_VARIANTS, NAV_PLACES, LANGUAGES, STYLE_PRESETS, STYLE_EDITABLE, uiState, saveUiPrefs, styleTokens, contrastRatio } from '@/helpers/uiPrefs.js'

// the floors the presets are built to (.claude/skills/ui-comfort): text on panels, outlines
const FLOORS = { ink: 7, line: 3 }

export default {
  name: 'UserMenu',

  emits: ['logout'],

  data () {
    return {
      THEMES,
      FONT_SIZES,
      ORDER_HIST_VARIANTS,
      NAV_PLACES,
      LANGUAGES,
      STYLE_EDITABLE,
      ui: uiState,
      open: false,
    }
  },

  computed: {
    user () {
      return this.$store.state.user
    },

    // presets by level, lighter first
    styleRows () {
      return ['light', 'calm'].map(level => ({ level, presets: STYLE_PRESETS.filter(s => s.level === level) }))
    },

    currentPreset () {
      return STYLE_PRESETS.find(s => s.key === this.ui.style) || STYLE_PRESETS[0]
    },

    // the colours the current style paints with (what the four pickers show)
    tokens () {
      return styleTokens({ ...this.ui, styleOn: true })
    },

    // contrast of the text and the outlines against the panels, flagged under the floor
    checks () {
      const out = {}
      Object.keys(FLOORS).forEach(k => {
        const r = contrastRatio(this.tokens[k], this.tokens.surface)
        out[k] = { text: r.toFixed(1) + ':1', low: r < FLOORS[k] }
      })
      return out
    },
  },

  mounted () {
    document.addEventListener('click', this.closeOutside)
  },

  beforeUnmount () {
    document.removeEventListener('click', this.closeOutside)
  },

  methods: {
    set (key, value) {
      saveUiPrefs({ ...this.ui, [key]: value })
      // the menu moved: the pages just changed size, let the ones that measure themselves do it again
      if (key === 'nav') this.$nextTick(() => window.dispatchEvent(new Event('resize')))
    },

    setCustom (hex) {
      saveUiPrefs({ ...this.ui, theme: 'custom', custom: hex })
    },

    presetName (s) {
      return this.$t('common.menu.style_' + s.hue) + ' · ' + this.$t('common.menu.styleLevel_' + s.level)
    },

    // changing a colour turns the current style into the user's own
    editStyle (key, hex) {
      const styleCustom = {}
      STYLE_EDITABLE.forEach(k => { styleCustom[k] = this.tokens[k] })
      styleCustom[key] = hex
      saveUiPrefs({ ...this.ui, styleOn: true, style: 'custom', styleCustom })
    },

    closeOutside (event) {
      const root = this.$refs.root
      if (this.open && root && !root.contains(event.target)) this.open = false
    },
  },
}
</script>
